import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Car,
  Clock,
  DollarSign,
  Shield,
  Star,
  CheckCircle,
  Mail,
  TrendingUp,
  Award,
  ChevronDown,
  Menu,
  X,
  Users
} from 'lucide-react';

const DriversLanding = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const driverBenefits = [
    {
      icon: TrendingUp,
      title: 'Competitive Earnings',
      description: 'Keep 80% of your earnings with our fair commission structure'
    },
    {
      icon: Clock,
      title: 'Flexible Schedule',
      description: 'Drive when you want, where you want - you\'re in control'
    },
    {
      icon: Award,
      title: 'Rating System',
      description: 'Build your reputation and earn more with 5-star ratings'
    },
    {
      icon: Users,
      title: 'Growing Community',
      description: 'Join a network of professional drivers and steady customers'
    }
  ];

  const faqs = [
    {
      question: 'What are the requirements to become a driver?',
      answer: 'You need: a valid driver\'s license, clean driving record, Black Suburban vehicle, and pass our background check. Monthly subscription is $49.99.'
    },
    {
      question: 'How much can I earn?',
      answer: 'Drivers keep 80% of fares. Average monthly earnings are $2,500+ based on 100 rides per month. Your earnings depend on how many hours you drive.'
    },
    {
      question: 'What is the monthly subscription fee?',
      answer: 'The all-inclusive subscription is $49.99/month. This gives you access to our platform, real-time navigation, customer base, and 24/7 driver support.'
    },
    {
      question: 'Do I need my own vehicle?',
      answer: 'Yes, you must have a Black Chevrolet Suburban in good condition. We require this specific vehicle to maintain our premium service standards.'
    },
    {
      question: 'How do I get paid?',
      answer: 'Payments are processed through Stripe. You keep 80% of each fare, and payouts are made weekly directly to your bank account.'
    },
    {
      question: 'Can I drive part-time?',
      answer: 'Absolutely! You have complete flexibility. Drive full-time, part-time, or just on weekends - you choose your own schedule.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <Car className="w-8 h-8 text-blue-600" />
              <span className="ml-2 text-2xl font-bold text-gray-900">Onyx Reserve</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('benefits')} className="text-gray-700 hover:text-blue-600 transition">
                Benefits
              </button>
              <button onClick={() => scrollToSection('earnings')} className="text-gray-700 hover:text-blue-600 transition">
                Earnings
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-blue-600 transition">
                FAQ
              </button>
              <button onClick={() => navigate('/')} className="text-gray-700 hover:text-blue-600 transition">
                Need a Ride?
              </button>
              <Button variant="outline" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button onClick={() => navigate('/register?role=driver')}>
                Apply Now
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-700">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3">
              <button onClick={() => scrollToSection('benefits')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">
                Benefits
              </button>
              <button onClick={() => scrollToSection('earnings')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">
                Earnings
              </button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">
                FAQ
              </button>
              <button onClick={() => navigate('/')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">
                Need a Ride?
              </button>
              <div className="px-4 space-y-2">
                <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                  Login
                </Button>
                <Button className="w-full" onClick={() => navigate('/register?role=driver')}>
                  Apply Now
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Drive & Earn with <span className="text-blue-600">Onyx Reserve</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Join our network of professional drivers. Set your own schedule, keep 80% of your earnings, and be part of a premium transportation service.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={() => navigate('/register?role=driver')} className="text-lg px-8">
                  Apply to Drive
                </Button>
                <Button size="lg" variant="outline" onClick={() => scrollToSection('earnings')} className="text-lg px-8">
                  See Earnings
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-6">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-gold-400 fill-current" />
                  <span className="ml-2 text-gray-600 font-semibold">4.9/5 Rating</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="ml-2 text-gray-600 font-semibold">500+ Drivers</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop" 
                alt="Professional Driver"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg text-gray-900">
                <div className="text-3xl font-bold text-blue-600">$2,500+</div>
                <div className="text-gray-600">Average Monthly Earnings</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Drive with Onyx Reserve?
            </h2>
            <p className="text-lg text-gray-600">
              Join a premium service that values professional drivers
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {driverBenefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition">
                <CardContent className="pt-6">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Section */}
      <section id="earnings" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Transparent Earnings
            </h2>
            <p className="text-xl text-blue-100">
              Know exactly what you&apos;ll earn - no hidden fees
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="pt-6 text-center">
                <DollarSign className="w-12 h-12 mx-auto mb-4" />
                <div className="text-4xl font-bold mb-2">$49.99</div>
                <div className="text-blue-100 mb-4">Monthly Subscription</div>
                <div className="text-sm text-blue-100">All-inclusive platform access</div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="pt-6 text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-4" />
                <div className="text-4xl font-bold mb-2">80%</div>
                <div className="text-blue-100 mb-4">You Keep</div>
                <div className="text-sm text-blue-100">Of every fare you earn</div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="pt-6 text-center">
                <Star className="w-12 h-12 mx-auto mb-4" />
                <div className="text-4xl font-bold mb-2">$2,500+</div>
                <div className="text-blue-100 mb-4">Average Monthly</div>
                <div className="text-sm text-blue-100">Based on 100 rides/month</div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 max-w-3xl mx-auto bg-white/10 backdrop-blur p-6 rounded-xl">
            <h3 className="text-2xl font-bold mb-4">What&apos;s Included:</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span>Access to our customer base and booking platform</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span>Real-time GPS tracking and navigation</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span>Secure payment processing through Stripe</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span>Rating and review system to build your reputation</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span>24/7 driver support and assistance</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span>Weekly direct deposits to your bank account</span>
              </li>
            </ul>
          </div>

          <div className="text-center mt-12">
            <Button size="lg" variant="secondary" onClick={() => navigate('/register?role=driver')}>
              Start Earning Today
            </Button>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Driver Requirements
            </h2>
            <p className="text-lg text-gray-600">
              We maintain high standards to ensure quality service
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Valid Driver&apos;s License</h3>
              <p className="text-gray-600">Must have a clean driving record with no major violations</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Background Check</h3>
              <p className="text-gray-600">Pass a comprehensive criminal and driving background check</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Black Suburban Required</h3>
              <p className="text-gray-600">Vehicle must be a Black Chevrolet Suburban in excellent condition</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Professional Service</h3>
              <p className="text-gray-600">Commitment to providing excellent customer service</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Driver FAQ
            </h2>
            <p className="text-lg text-gray-600">
              Common questions from prospective drivers
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-gray-100">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === index ? 'transform rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Start Driving?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join Onyx Reserve today and start earning on your own schedule
          </p>
          <Button size="lg" variant="secondary" onClick={() => navigate('/register?role=driver')} className="text-lg px-8">
            Apply Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4 cursor-pointer" onClick={() => navigate('/')}>
                <Car className="w-8 h-8 text-blue-400" />
                <span className="ml-2 text-2xl font-bold">Onyx Reserve</span>
              </div>
              <p className="text-gray-400">Professional driving opportunities</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate('/')} className="hover:text-white transition">Need a Ride?</button></li>
                <li><button onClick={() => scrollToSection('benefits')} className="hover:text-white transition">Benefits</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-white transition">FAQ</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>support@onyxreserve.net</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Onyx Reserve. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DriversLanding;
